import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCalls } from '../store/app/conversations/conversationSlice';
import ConversationItem from './ConversationItem';
import { Icon } from '@iconify/react';
import { ConversationShimmer, MessageShimmer, EmptyState } from "../utils/utils";
import ConvsersationSummaryTab from "./child/ConversationSummaryTab";
import { fetchCallDetails } from '../utils/api/callAPI';
import { getAllAssistant } from '../utils/api/assistantAPI';
import { apiToken } from '../utils/config';
import { motion } from 'framer-motion';

const Conversation = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.conversation.value);
    const status = useSelector((state) => state.conversation.status);
    const { id: selectedId } = useParams();
    // Refs and States
    const [currentId, setCurrentId] = useState(selectedId);
    const [messageData, setMessageData] = useState([]);
    const [isMessageLoading, setIsMessageLoading] = useState(false);
    const [recordingUrl, setRecordingUrl] = useState('');
    const [conversationData, setConversationData] = useState();
    const [assistants, setAssistants] = useState([]);
    const [assistantLoading, setAssistantLoading] = useState(true);
    const [selectedAssistantId, setSelectedAssistantId] = useState('');
    const [assistantError, setAssistantError] = useState('');
    const conversations = data || [];

    // Ref to keep track of mounted status (avoids state updates on unmounted component)
    const isMounted = useRef(true);

    // Fetch messages and conversation data
    const loadCallDetails = useCallback((id) => {
        if (!id) return;
        setIsMessageLoading(true);
        fetchCallDetails(id,  {
            setIsMessageLoading,
            setRecordingUrl,
            setConversationData,
            setMessageData,
        });
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const res = await getAllAssistant();
            const list = res.assistants || [];
            setAssistants(list);
    
            if (list?.length > 0) {
                const firstId = list[0].id;
                setSelectedAssistantId(firstId);
            }
    
            setAssistantLoading(false);
        };
    
        fetchData();
    }, []);
    // Fetch calls when assistant changes
useEffect(() => {
    if (selectedAssistantId) {
        dispatch(fetchCalls(selectedAssistantId));
        setCurrentId(null); // reset currentId to avoid stale selection
    }
}, [selectedAssistantId, dispatch]);
// console.log(assistants)

// Automatically select the first conversation after new data arrives
useEffect(() => {
    if (data && data?.length > 0) {
        setCurrentId(data[0].id);
    }
}, [data]);

    useEffect(() => {
        if (selectedAssistantId) {
            dispatch(fetchCalls(selectedAssistantId));
        }
    }, [selectedAssistantId, dispatch]);


    // Sync currentId from URL params
    useEffect(() => {
        if (selectedId && selectedId !== currentId) {
            setCurrentId(selectedId);
        }
    }, [selectedId]);

    // Handle first available conversation after data load
    useEffect(() => {
        if (conversations?.length > 0 && !currentId) {
            const firstId = data[0].id;
            setCurrentId(firstId);
        }
    }, [data, currentId]);

    // Load details on change of currentId
    useEffect(() => {
        if (currentId) {
            loadCallDetails(currentId);
        }
    }, [currentId, loadCallDetails]);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const isLoading = status === 'loading';
    const isDataEmpty = data?.length === 0 && !isLoading;
    const isMessageDataEmpty = messageData?.length === 0 && !isMessageLoading;

    // Memoized conversation list for performance
    const conversationList = useMemo(() => {
        return data?.map((item) => (
            <li
                key={item.id}
                className={`mb-8 cursor-pointer transition-all hover:bg-light rounded-pill p-2 ${currentId === item.id ? 'bg-light-primary' : ''}`}
                onClick={() => setCurrentId(item.id)}
            >
                <ConversationItem
                    id={item.id}
                    startedAt={item.startedAt}
                    endedAt={item.endedAt}
                    isSelected={currentId === item.id}
                />
            </li>
        ));
    }, [data, currentId]);
    if (assistantLoading || !selectedAssistantId) {
        return (
          <div className="d-flex justify-content-center align-items-center vh-100">
            <p className="text-muted">Loading conversations...</p>
          </div>
        );
      }
      else{
    return (
        <motion.div 
        initial={{
            opacity: 0
        }}
        animate={{
            opacity: 1
        }}
        transition={{
            duration: 1
        }}
        className="row gy-4 flex-wrap-reverse">
            {/* LEFT SIDEBAR: Conversation list */}
            <div className="col-xxl-3 col-lg-4">
                <div className="card h-100 p-0 rounded-4 shadow-sm">
                    <div className="card-body p-0">
                        <div className="p-20">
                            <div className="position-relative">
                                <Icon icon="lucide:bot" width="18" height="18" style={iconStyle} />
                                <select
                                    name="assistant_id"
                                    value={selectedAssistantId}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setSelectedAssistantId(id);
                                    }}
                                    className="form-control"
                                    required
                                    style={selectStyle}
                                >
                                    <option value="">Select Assistant</option>
                                    {assistants?.map((assistant) => (
                                        <option key={assistant.id} value={assistant.id}>
                                            {assistant.name}
                                        </option>
                                    ))}
                                </select>
                                {assistantError && <div className="text-danger">{assistantError}</div>}
                                <Icon icon="lucide:chevron-down" width="18" height="18" style={dropdownIconStyle} />
                            </div>
                        </div>
                        <div className="conversation-list-container">
                            {isLoading ? (
                                <ConversationShimmer />
                            ) : isDataEmpty ? (
                                <EmptyState type="conversations" />
                            ) : (
                                <ul className="ai-chat-list scroll-sm pe-24 ps-24 pb-24">
                                    {conversationList}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* MIDDLE SECTION: Chat messages */}
            <div className="col-xxl-6 col-lg-4">
                <div className="chat-main card overflow-hidden rounded-4 shadow-sm">
                    {/* Chat header */}
                    <div className="chat-sidebar-single gap-8 justify-content-between cursor-default flex-nowrap p-3 border-bottom">
                        <div className="d-flex align-items-center gap-16">
                            <h6 className="text-lg mb-0 text-line-1">
                                {currentId ? (
                                    <>
                                        Conversation:{' '}
                                        <span style={{
                                            display: 'inline-block',
                                            backgroundColor: '#e0e0e0',
                                            color: '#333',
                                            fontSize: '14px',
                                            padding: '2px 8px',
                                            borderRadius: '6px',
                                        }}>
                                            {currentId}
                                        </span>
                                    </>
                                ) : 'Select a conversation'}
                            </h6>
                        </div>
                        {currentId && (
                            <div className="action-buttons d-flex gap-2">
                                <button
                                    className="btn btn-sm btn-light-primary rounded-circle"
                                    title="Copy ID"
                                    onClick={() => navigator.clipboard.writeText(currentId)}
                                >
                                    <i className="ri-file-copy-line"></i>
                                </button>
                                <button
                                    className="btn btn-sm btn-light-primary rounded-circle"
                                    title="Refresh"
                                    onClick={() => loadCallDetails(currentId)}
                                >
                                    <i className="ri-refresh-line"></i>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Message list */}
                    <div className="chat-message-list max-h-612-px min-h-612-px p-24 overflow-auto">
                        {!currentId ? (
                            <div className="d-flex align-items-center justify-content-center h-100">
                                <div className="text-center">
                                    <i className="ri-chat-3-line text-5xl text-secondary mb-3"></i>
                                    <p>Select a conversation to view messages</p>
                                </div>
                            </div>
                        ) : isMessageLoading ? (
                            <MessageShimmer />
                        ) : isMessageDataEmpty ? (
                            <EmptyState type="messages" />
                        ) : (
                            messageData?.map((message, index) => {
                                const isUser = message.role === 'user';
                                return (
                                    <div
                                        key={index}
                                        className={`d-flex gap-16 mb-16 ${isUser ? 'justify-content-end' : 'justify-content-start'}`}
                                    >
                                        {!isUser && (
                                            <div className="img overflow-hidden flex-shrink-0">
                                                <img
                                                    src="assets/images/favicon.png"
                                                    alt="icon"
                                                    className="w-44-px h-44-px rounded-circle object-fit-cover"
                                                />
                                            </div>
                                        )}
                                        <div
                                            style={{
                                                maxWidth: '75%',
                                                padding: '8px 16px',
                                                borderRadius: '10px',
                                                boxShadow: '0 .125rem .25rem rgba(0,0,0,.075)',
                                                backgroundColor: isUser ? '#3d8bfd' : '#f8f9fa',
                                                color: isUser ? '#ffffff' : '#212529'
                                            }}
                                        >
                                            <h6 className="text-sm mb-4 fw-bold" style={{ color: isUser ? 'white' : 'black' }}>
                                                {isUser ? 'User' : 'AI Assistant'}
                                            </h6>
                                            <p className="mb-0 text-sm white-space-pre-wrap">{message.message}</p>
                                        </div>
                                        {isUser && (
                                            <div
                                                style={{
                                                    width: '44px',
                                                    height: '44px',
                                                    borderRadius: '50%',
                                                    backgroundColor: '#0d6efd',
                                                    color: '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <Icon icon="mdi:account" width="24" height="24" />
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Audio recording section */}
                    {currentId && recordingUrl && (
                        <div className="chat-input border-top p-3">
                            <div className="d-flex align-items-center gap-3 w-100">
                                {isMessageLoading ? (
                                    <div className="w-100 shimmer-loader" />
                                ) : (
                                    <audio controls src={recordingUrl} className="w-100" preload="auto">
                                        Your browser does not support the audio element.
                                    </audio>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* RIGHT SIDEBAR: Summary tab */}
            {currentId && (
                <ConvsersationSummaryTab
                    conversationData={conversationData}
                    currentId={currentId}
                    isLoading={isMessageLoading}
                />
            )}
        </motion.div>
    );
      }

};

const labelStyle = {
    fontWeight: '500',
    marginBottom: '8px',
    color: '#344767',
    fontSize: '14px'
};

const iconStyle = {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6c757d'
};

const inputStyle = {
    paddingLeft: '40px',
    height: '45px',
    borderColor: '#e1e5eb',
    fontSize: '14px'
};

const selectStyle = {
    paddingLeft: '40px',
    height: '45px',
    appearance: 'none',
    borderColor: '#e1e5eb',
    fontSize: '14px'
};

const dropdownIconStyle = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: '#6c757d'
};

const footerStyle = {
    background: '#ffffff',
    padding: '20px 25px',
    borderTop: '1px solid #eaeaea',
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
    display: 'flex',
    justifyContent: 'flex-end'
};

const submitBtnStyle = {
    padding: '12px 30px',
    fontWeight: '500',
    fontSize: '15px',
    borderRadius: '6px',
    background: 'linear-gradient(135deg, #6259ca 0%, #4e4ac8 100%)',
    border: 'none',
    boxShadow: '0 4px 8px rgba(98, 89, 202, 0.3)',
    transition: 'all 0.3s ease',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
};

const cardStyle = {
    borderRadius: '10px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    border: 'none'
};

const cardHeaderStyle = {
    background: '#f8f9fa',
    borderBottom: '1px solid #eaeaea',
    padding: '20px 25px'
};

const cardBodyStyle = {
    padding: '30px 25px',
    background: '#ffffff'
};

const titleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#344767'
};


export default Conversation;
